import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();

      if (this.id !== 'search-modal') {
        if (!dialog.hasAttribute('tabindex')) {
          dialog.setAttribute('tabindex', '-1');
        }
        dialog.focus({ preventScroll: true });
      }

      this.dispatchEvent(new DialogOpenEvent());

      // Backdrop clicks target the <dialog> in the top layer and often never
      // bubble to dialog-component. Listen on the dialog and in capture phase.
      dialog.addEventListener('click', this.#handleDialogClick);
      dialog.addEventListener('cancel', this.#handleCancel);
      document.addEventListener('pointerdown', this.#handleDocumentPointerDown, true);
      dialog.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open || dialog.classList.contains('dialog-closing')) return;

    dialog.removeEventListener('click', this.#handleDialogClick);
    dialog.removeEventListener('cancel', this.#handleCancel);
    document.removeEventListener('pointerdown', this.#handleDocumentPointerDown, true);
    dialog.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Opens the dialog and scrolls to a filter panel.
   *
   * @param {{ filterId?: string }} [data]
   * @param {Event} [event]
   */
  showFilter(data, event) {
    event?.preventDefault();

    const filterId = data?.filterId;
    const { dialog } = this.refs;

    if (!dialog.open) {
      this.showDialog();
    }

    requestAnimationFrame(() => {
      this.#focusFilter(filterId);
    });
  }

  /**
   * @param {string | undefined} filterId
   */
  #focusFilter(filterId) {
    if (!filterId) return;

    const details = this.querySelector(`#${CSS.escape(filterId)}`);
    if (!(details instanceof HTMLDetailsElement)) return;

    details.open = true;
    details.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    details.querySelector('summary')?.focus({ preventScroll: true });
  }

  /**
   * Closes the dialog when the user clicks the backdrop.
   * Backdrop hits set event.target to the dialog element itself.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleDialogClick = (event) => {
    if (event.target === this.refs.dialog) {
      this.closeDialog();
    }
  };

  /**
   * Intercepts native dismiss (Escape / backdrop in supporting browsers)
   * so the themed close animation can run.
   *
   * @param {Event} event - The cancel event.
   */
  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Capture-phase fallback for backdrop clicks that never reach bubble listeners.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  #handleDocumentPointerDown = (event) => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    if (event.target === dialog) {
      this.closeDialog();
      return;
    }

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
